import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { adminAPI } from '../../api';
import { useToast } from '../../hooks/useToast';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Textarea } from '../../components/ui/Textarea';
import { ChevronDown, ChevronUp, ArrowLeft, Loader2, MessageSquare, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
];

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function SeverityBadge({ severity }) {
  const variants = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return <Badge className={variants[severity] || variants.low}>{severity}</Badge>;
}

function StatusBadge({ status }) {
  const variants = {
    new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    investigating: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    rejected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return <Badge className={variants[status] || variants.new}>{status}</Badge>;
}

function formatActor(activity) {
  if (!activity.actor) return 'System';
  const { firstName, lastName, email } = activity.actor;
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || email || 'Unknown';
}

export default function AdminErrorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: toastError, ToastContainer } = useToast();
  const [group, setGroup] = useState(null);
  const [latestEvent, setLatestEvent] = useState(null);
  const [activity, setActivity] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsPagination, setEventsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [saving, setSaving] = useState(false);
  const [stackExpanded, setStackExpanded] = useState(false);

  const g = group || {};
  const statusVal = status || g.status;
  const severityVal = severity || g.severity;
  const mutedVal = isMuted !== false && (isMuted || g.isMuted);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setListError('');
      const data = await adminAPI.getErrorGroup(id);
      setGroup(data.group);
      setLatestEvent(data.latestEvent || null);
      setStatus(data.group?.status ?? '');
      setSeverity(data.group?.severity ?? '');
      setIsMuted(data.group?.isMuted ?? false);
    } catch (err) {
      setListError(err?.message || 'Failed to load error group');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchActivity = useCallback(async () => {
    if (!id) return;
    try {
      const data = await adminAPI.getErrorGroupActivity(id);
      setActivity(data.activity || []);
    } catch {
      setActivity([]);
    }
  }, [id]);

  const fetchEvents = useCallback(async () => {
    if (!id) return;
    try {
      const data = await adminAPI.getErrorGroupEvents(id, {
        page: eventsPagination.page,
        limit: eventsPagination.limit,
      });
      setEvents(data.events || []);
      setEventsPagination((prev) => ({ ...prev, ...(data.pagination || {}) }));
    } catch {
      setEvents([]);
    }
  }, [id, eventsPagination.page, eventsPagination.limit]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (group) {
      fetchActivity();
      fetchEvents();
    }
  }, [group?.id, fetchActivity, fetchEvents]);

  const handleUpdate = async (payload) => {
    if (!id) return;
    try {
      setSaving(true);
      await adminAPI.updateErrorGroup(id, payload);
      success('Updated', 'Error group updated.');
      fetchDetail();
      fetchActivity();
    } catch (err) {
      toastError('Update failed', err?.message || 'Could not update.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (e) => {
    const v = e.target.value;
    setStatus(v);
    handleUpdate({ status: v });
  };

  const handleSeverityChange = (e) => {
    const v = e.target.value;
    setSeverity(v);
    handleUpdate({ severity: v });
  };

  const handleMuteToggle = () => {
    const next = !mutedVal;
    setIsMuted(next);
    handleUpdate({ isMuted: next });
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      setSaving(true);
      await adminAPI.addErrorGroupComment(id, commentText.trim());
      success('Comment added', '');
      setCommentText('');
      fetchActivity();
    } catch (err) {
      toastError('Failed', err?.message || 'Could not add comment.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !group) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (listError && !group) {
    return (
      <Layout>
        <div className="space-y-4">
          <Button variant="outline" onClick={() => navigate('/admin/errors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to list
          </Button>
          <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
            {listError}
          </div>
        </div>
      </Layout>
    );
  }

  const ev = latestEvent || {};

  return (
    <Layout>
      <ToastContainer />
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/errors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to list
          </Button>
        </div>

        {/* Summary */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Title</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 break-words">{g.title || g.fingerprint || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Severity</p>
                <div className="mt-0.5">
                  <SeverityBadge severity={g.severity} />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</p>
                <div className="mt-0.5">
                  <StatusBadge status={g.status} />
                  {g.isMuted && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(muted)</span>}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">First / Last seen</p>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {g.firstSeen || g.first_seen ? format(new Date(g.firstSeen || g.first_seen), 'dd MMM yyyy HH:mm') : '—'}
                  {' / '}
                  {g.lastSeen || g.last_seen ? format(new Date(g.lastSeen || g.last_seen), 'dd MMM yyyy HH:mm') : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Count total / 24h</p>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {g.countTotal ?? g.count_total ?? 0} / {g.count24h ?? g.count_24h ?? 0}
                </p>
              </div>
              {(g.sentryUrl || g.sentryIssueId) && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Sentry</p>
                  {g.sentryUrl ? (
                    <a
                      href={g.sentryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View in Sentry <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-mono truncate max-w-[200px]" title={g.sentryIssueId}>
                      {g.sentryIssueId}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Actions</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-40">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={statusVal}
                  onChange={handleStatusChange}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="w-40">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Severity</label>
                <select
                  value={severityVal}
                  onChange={handleSeverityChange}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {SEVERITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <Button variant="outline" size="sm" onClick={handleMuteToggle} disabled={saving}>
                {mutedVal ? 'Unmute' : 'Mute'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Latest occurrence */}
        {(ev.message || ev.stack) && (
          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <CardHeader className="border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Latest occurrence</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {ev.message && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Message</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-mono break-words whitespace-pre-wrap">
                    {ev.message}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {ev.route && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Route</p>
                    <p className="text-gray-900 dark:text-gray-100 font-mono break-all">{ev.route}</p>
                  </div>
                )}
                {ev.requestId && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Request ID</p>
                    <p className="text-gray-900 dark:text-gray-100 font-mono break-all">{ev.requestId}</p>
                  </div>
                )}
                {ev.buildVersion && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Build version</p>
                    <p className="text-gray-900 dark:text-gray-100">{ev.buildVersion}</p>
                  </div>
                )}
              </div>
              {ev.stack && (
                <div>
                  <button
                    type="button"
                    onClick={() => setStackExpanded(!stackExpanded)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    {stackExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    Stack trace
                  </button>
                  {stackExpanded && (
                    <pre className="mt-2 p-3 rounded-md bg-gray-100 dark:bg-gray-900 text-xs text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                      {ev.stack}
                    </pre>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Comments / Add comment */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700 flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Activity & comments
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add comment
              </label>
              <div className="flex gap-2">
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  placeholder="Add a comment..."
                  className="flex-1 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                />
                <Button onClick={handleAddComment} disabled={saving || !commentText.trim()}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                </Button>
              </div>
            </div>
            {activity.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Timeline</p>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {activity.map((a) => (
                    <div key={a.id} className="py-2 first:pt-0">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {a.actionType === 'comment' ? (
                          <span>{formatActor(a)}: {a.commentText}</span>
                        ) : (
                          <span>
                            {formatActor(a)}: {a.actionType}
                            {a.fromValue != null && a.toValue != null && (
                              <span className="text-gray-500 dark:text-gray-400"> {a.fromValue} → {a.toValue}</span>
                            )}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {a.createdAt ? format(new Date(a.createdAt), 'dd MMM yyyy HH:mm') : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Occurrences list */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Occurrences</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {events.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No occurrences.</p>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Occurred at
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Route
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Message
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {events.map((e) => (
                        <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {e.occurredAt ? format(new Date(e.occurredAt), 'dd MMM yyyy HH:mm') : '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate font-mono">
                            {e.route || '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-md truncate">
                            {e.message || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {eventsPagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Page {eventsPagination.page} of {eventsPagination.totalPages} ({eventsPagination.total} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={eventsPagination.page <= 1}
                        onClick={() => setEventsPagination((p) => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={eventsPagination.page >= eventsPagination.totalPages}
                        onClick={() => setEventsPagination((p) => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
